import React, { useCallback, useEffect, useMemo, useState } from "react";
import CrudModule from "@/components/admin/CrudModule";
import { Badge } from "@/components/common/Primitives";
import { unitsCrud, unitPostsCrud } from "@/services/unitsService";
import { storageService } from "@/services/storageService";

export default function AdminUnitsPage() {
  const [tab, setTab] = useState("units");
  const [units, setUnits] = useState([]);

  const syncUnits = useCallback((rows) => {
    setUnits(
      [...rows].sort(
        (a, b) =>
          (a.sort_order || 0) - (b.sort_order || 0) ||
          (a.name || "").localeCompare(b.name || "")
      )
    );
  }, []);

  useEffect(() => {
    unitsCrud.list().then(syncUnits).catch(() => setUnits([]));
  }, [syncUnits]);

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.slug, label: u.name || u.slug })),
    [units]
  );

  const unitsBySlug = useMemo(
    () => Object.fromEntries(units.map((u) => [u.slug, u.name || u.slug])),
    [units]
  );

  const unitsDescription = useMemo(() => {
    const count =
      units.length > 0 ? units.length.toLocaleString("en-IN") : "…";
    return `The ${count} BCS parish units — each gets its own public page automatically.`;
  }, [units.length]);

  const unitPostFields = useMemo(
    () => [
      { name: "title", label: "Title", type: "text", required: true },
      {
        name: "unit_slug",
        label: "Unit Name",
        type: "select",
        options: unitOptions,
        required: true,
        hint: "Select the parish unit this post belongs to.",
      },
      {
        name: "image_url",
        label: "Image",
        type: "image",
        onUpload: (file, hint) => storageService.uploadUnitPostImage(file, hint),
        folderLabel: "units_post",
        nameHintFrom: "title",
      },
      { name: "body", label: "Body", type: "textarea", required: true },
      { name: "is_published", label: "Published", type: "checkbox", default: true },
    ],
    [unitOptions]
  );

  const unitPostColumns = useMemo(
    () => [
      { key: "title", label: "Title" },
      {
        key: "unit_slug",
        label: "Unit",
        render: (r) => unitsBySlug[r.unit_slug] || r.unit_slug || "—",
      },
      { key: "created_at", label: "Date", render: (r) => String(r.created_at || "").slice(0, 10) },
    ],
    [unitsBySlug]
  );

  return (
    <div data-testid="admin-units-page">
      <div className="flex gap-2 mb-6">
        {[
          { id: "units", label: "Units" },
          { id: "posts", label: "Unit Posts" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === t.id ? "bg-maroon text-white" : "bg-white border border-gray-200 text-ink-soft hover:text-maroon"
            }`}
            data-testid={`units-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "units" ? (
        <CrudModule
          title="Units"
          description={unitsDescription}
          crud={unitsCrud}
          onRowsChange={syncUnits}
          searchKeys={["name", "parish_name", "deanery", "area"]}
          columns={[
            { key: "parish_name", label: "Parish" },
            { key: "deanery", label: "Deanery", render: (r) => <Badge tone="maroon">{r.deanery}</Badge> },
            { key: "area", label: "Area" },
            { key: "member_count", label: "Members" },
          ]}
          toggles={[
            { key: "is_published", label: "Published" },
            { key: "is_featured", label: "Featured" },
          ]}
          fields={[
            { name: "name", label: "Unit Name", type: "text", required: true, placeholder: "BCS Unit – St. Peter's Church" },
            { name: "parish_name", label: "Parish Name", type: "text", required: true },
            { name: "slug", label: "Slug", type: "text", required: true, hint: "URL identifier, e.g. st-peter-s-church" },
            { name: "deanery", label: "Deanery", type: "text", required: true },
            { name: "area", label: "Area", type: "text", required: true },
            { name: "address", label: "Address", type: "text" },
            { name: "president", label: "Unit President", type: "text" },
            { name: "contact_phone", label: "Contact Phone", type: "text" },
            { name: "contact_email", label: "Contact Email", type: "text" },
            {
              name: "image_url",
              label: "Unit Image",
              type: "image",
              onUpload: (file, hint) => storageService.uploadUnitImage(file, hint),
              folderLabel: "units",
              nameHintFrom: "slug",
            },
            { name: "established_year", label: "Established Year", type: "number" },
            { name: "member_count", label: "Member Count", type: "number" },
            { name: "sort_order", label: "Sort Order", type: "number" },
            { name: "description", label: "Description", type: "textarea" },
            { name: "is_published", label: "Published", type: "checkbox", default: true },
            { name: "is_featured", label: "Featured", type: "checkbox", default: false },
          ]}
        />
      ) : (
        <CrudModule
          title="Unit Posts"
          description="Updates and media published on individual unit pages."
          crud={unitPostsCrud}
          searchKeys={["title", "unit_slug"]}
          columns={unitPostColumns}
          toggles={[{ key: "is_published", label: "Published" }]}
          fields={unitPostFields}
        />
      )}
    </div>
  );
}
